import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, List, GanttChart, MapPin, Users, Truck, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, DataState, Empty, SiteStatusBadge, NativeSelect } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SiteForm } from "../components/Forms";
import { eur, fmtDate, parseISO, todayISO } from "../lib/format";
import { cn } from "../lib/utils";
import { BudgetMeter } from "../components/BudgetAlert";

const BAR = { attivo: "bg-emerald-500", da_avviare: "bg-slate-400", sospeso: "bg-amber-500", chiuso: "bg-indigo-500" };
const STATUS_LABEL = { attivo: "Attivo", da_avviare: "Da avviare", sospeso: "Sospeso", chiuso: "Chiuso" };
const siteEnd = (s) => s.end_actual || s.end_planned || s.start_date;
const minIso = (a, b) => (a < b ? a : b);
const maxIso = (a, b) => (a > b ? a : b);

function ganttScale(rows) {
  const ds = rows.flatMap((s) => [s.start_date, siteEnd(s)]);
  const mn = parseISO(ds.reduce(minIso, todayISO()));
  const mx = parseISO(ds.reduce(maxIso, todayISO()));
  const min = new Date(mn.getFullYear(), mn.getMonth(), 1);
  const max = new Date(mx.getFullYear(), mx.getMonth() + 1, 0);
  const months = [];
  for (let d = new Date(min); d <= max; d.setMonth(d.getMonth() + 1)) months.push(new Date(d));
  const span = max - min;
  return { min, months, span, pos: (iso) => ((parseISO(iso) - min) / span) * 100 };
}

const monthLeft = (scale, m) => `${((m - scale.min) / scale.span) * 100}%`;

function GanttRow({ s, scale, todayPos, onOpen }) {
  const end = siteEnd(s);
  const left = scale.pos(s.start_date);
  return (
    <div className="grid grid-cols-[220px_1fr] items-center py-2.5 border-b border-border/40 hover:bg-accent/30 cursor-pointer" onClick={() => onOpen(s.id)} data-testid={`gantt-row-${s.id}`}>
      <div className="pr-3 truncate"><div className="text-sm font-medium truncate">{s.name}</div><div className="text-[11px] text-muted-foreground font-mono">{s.code}</div></div>
      <div className="relative h-8">
        {scale.months.map((m) => <span key={m.toISOString()} className="absolute top-0 bottom-0 w-px bg-border/40" style={{ left: monthLeft(scale, m) }} />)}
        <div className={cn("absolute top-1.5 h-5 rounded-md text-[11px] font-semibold text-slate-950 px-2 flex items-center truncate", BAR[s.status])}
          style={{ left: `${left}%`, width: `${Math.max(scale.pos(end) - left, 1)}%` }} title={`${fmtDate(s.start_date)} → ${fmtDate(end)}`}>
          {fmtDate(s.start_date)} → {fmtDate(end)}
        </div>
        <span className="absolute top-0 bottom-0 w-0.5 bg-red-500" style={{ left: `${todayPos}%` }} />
      </div>
    </div>
  );
}

const GanttLegend = () => (
  <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
    {Object.entries(STATUS_LABEL).map(([k, l]) => (
      <span key={k} className="flex items-center gap-1.5"><span className={cn("h-2.5 w-2.5 rounded-sm", BAR[k])} />{l}</span>
    ))}
    <span className="flex items-center gap-1.5"><span className="h-3 w-0.5 bg-red-500" />Oggi</span>
  </div>
);

export function Gantt({ sites }) {
  const nav = useNavigate();
  const rows = useMemo(() => sites.filter((s) => s.start_date), [sites]);
  const scale = useMemo(() => ganttScale(rows), [rows]);
  if (!rows.length) return <Empty title="Nessun cantiere con date pianificate" />;
  const todayPos = scale.pos(todayISO());
  return (
    <div className="surface p-4 overflow-x-auto scrollbar-thin" data-testid="sites-gantt">
      <div className="min-w-[900px]">
        <div className="grid grid-cols-[220px_1fr] text-xs text-muted-foreground border-b border-border pb-2">
          <div>Cantiere</div>
          <div className="relative h-4">{scale.months.map((m) => (
            <span key={m.toISOString()} className="absolute capitalize" style={{ left: monthLeft(scale, m) }}>{m.toLocaleDateString("it-IT", { month: "short", year: "2-digit" })}</span>
          ))}</div>
        </div>
        {rows.map((s) => <GanttRow key={s.id} s={s} scale={scale} todayPos={todayPos} onOpen={(id) => nav(`/cantieri/${id}`)} />)}
        <GanttLegend />
      </div>
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div className="flex rounded-lg border border-border p-0.5">
      <Button size="sm" variant={view === "list" ? "secondary" : "ghost"} onClick={() => setView("list")} data-testid="sites-view-list"><List className="h-4 w-4 mr-1" />Elenco</Button>
      <Button size="sm" variant={view === "gantt" ? "secondary" : "ghost"} onClick={() => setView("gantt")} data-testid="sites-view-gantt"><GanttChart className="h-4 w-4 mr-1" />Gantt</Button>
    </div>
  );
}

const NO_TYPE = "__none__";

function SitesFilters({ q, setQ, siteType, setSiteType, status, setStatus, siteTypes, missing }) {
  return (
    <div className="surface p-3 mb-4 grid gap-2 sm:grid-cols-[1fr_200px_200px]">
      <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9 h-10" placeholder="Cerca codice, nome, cliente, indirizzo, tipologia…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sites-search-input" /></div>
      <NativeSelect value={siteType} onChange={(e) => setSiteType(e.target.value)} data-testid="sites-type-filter">
        <option value="">Tutte le tipologie</option>
        {missing > 0 && <option value={NO_TYPE}>Senza tipologia ({missing})</option>}
        {siteTypes?.map((t) => <option key={t}>{t}</option>)}
      </NativeSelect>
      <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sites-status-filter">
        <option value="">Tutti gli stati</option><option value="da_avviare">Da Avviare</option><option value="attivo">Attivo</option><option value="sospeso">Sospeso</option><option value="chiuso">Chiuso</option>
      </NativeSelect>
    </div>
  );
}

function MissingTypeBanner({ count, active, onShow }) {
  if (!count) return null;
  return (
    <div className="surface p-3 mb-4 flex flex-wrap items-center justify-between gap-3 border-amber-600/60" data-testid="sites-missing-type-banner">
      <span className="flex items-center gap-2 text-sm"><Tag className="h-4 w-4 text-amber-400" /><b>{count} {count === 1 ? "cantiere" : "cantieri"} senza tipologia</b><span className="text-muted-foreground">· sceglila direttamente dalla scheda qui sotto</span></span>
      <Button size="sm" variant="outline" onClick={onShow} data-testid="sites-missing-type-show-btn">{active ? "Mostra tutti" : "Mostra solo questi"}</Button>
    </div>
  );
}

function QuickTypeSelect({ s, siteTypes, onSaved }) {
  const [busy, setBusy] = useState(false);
  const save = async (value) => {
    if (!value) return;
    setBusy(true);
    try {
      await api.patch(`/sites/${s.id}/type`, { site_type: value });
      toast.success(`✓ ${s.code}: tipologia "${value}" salvata`);
      onSaved();
    } catch (e) {
      toast.error(`✕ ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <NativeSelect value="" disabled={busy} onChange={(e) => save(e.target.value)} className="h-9 text-sm border-amber-600/60 bg-amber-950/30" data-testid={`site-quick-type-${s.id}`}>
        <option value="">{busy ? "Salvataggio…" : "Senza tipologia · scegli…"}</option>
        {siteTypes.map((t) => <option key={t} value={t}>{t}</option>)}
      </NativeSelect>
    </div>
  );
}

function SiteCard({ s, i, onOpen, quickType }) {
  const open = () => onOpen(s.id);
  return (
    <div role="button" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}
      className="surface p-5 text-left hover:border-amber-500/60 transition-colors fade-up cursor-pointer" style={{ animationDelay: `${i * 40}ms` }} data-testid={`site-card-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div><div className="text-xs font-mono text-amber-400">{s.code}</div><div className="text-lg font-semibold font-heading">{s.name}</div></div>
        <SiteStatusBadge status={s.status} />
      </div>
      <div className="text-sm text-muted-foreground mt-1">{s.client}</div>
      {s.site_type && <div className="mt-2"><span className="text-[11px] uppercase tracking-wide rounded border border-amber-600/50 bg-amber-950/40 text-amber-200 px-1.5 py-0.5" data-testid={`site-type-badge-${s.id}`}>{s.site_type}</span></div>}
      {!s.site_type && quickType && quickType(s)}
      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.address}</div>
      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border/60 text-sm">
        <div><div className="eyebrow">Oggi</div><div className="num flex items-center gap-1 mt-1"><Users className="h-3.5 w-3.5" />{s.workers_today} · <Truck className="h-3.5 w-3.5" />{s.vehicles_today}</div></div>
        <div><div className="eyebrow">Resp.</div><div className="truncate mt-1">{s.manager_name || "—"}</div></div>
        <div className="text-right"><div className="eyebrow">Budget</div><div className="num mt-1">{eur(s.budget)}</div></div>
      </div>
      <div className="text-xs text-muted-foreground mt-3 num">{fmtDate(s.start_date)} → {fmtDate(s.end_actual || s.end_planned)}</div>
      <BudgetMeter usage={s.budget_usage} className="mt-3" testid={`site-budget-meter-${s.id}`} />
    </div>
  );
}

function SitesBody({ view, sites, onOpen, quickType }) {
  if (view === "gantt") return <Gantt sites={sites} />;
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {sites.map((s, i) => <SiteCard key={s.id} s={s} i={i} onOpen={onOpen} quickType={quickType} />)}
    </div>
  );
}

function useSitesPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [siteType, setSiteType] = useState("");
  const { data, loading, reload } = useApi("/sites", { q, status, site_type: siteType });
  const isAdmin = user.role === "admin";
  const types = data?.site_types || [];
  return {
    isAdmin, data, loading, reload, q, setQ, status, setStatus, siteType, setSiteType, types,
    sites: data?.items || [], missing: data?.missing_type_count || 0, onlyMissing: siteType === NO_TYPE,
    openSite: (id) => nav(`/cantieri/${id}`),
    quickType: isAdmin ? (s) => <QuickTypeSelect s={s} siteTypes={types} onSaved={reload} /> : null,
  };
}

function SitesHeader({ isAdmin, view, setView, onNew }) {
  return (
    <PageHeader eyebrow="Anagrafica" title={isAdmin ? "Cantieri" : "I miei cantieri"} subtitle="Stato, responsabili, risorse e costi di ogni cantiere"
      actions={<>
        <ViewToggle view={view} setView={setView} />
        {isAdmin && <Button onClick={onNew} data-testid="sites-new-btn"><Plus className="h-4 w-4 mr-2" />Nuovo cantiere</Button>}
      </>} />
  );
}

export default function Sites() {
  const p = useSitesPage();
  const [view, setView] = useState("list");
  const [open, setOpen] = useState(false);
  return (
    <div>
      <SitesHeader isAdmin={p.isAdmin} view={view} setView={setView} onNew={() => setOpen(true)} />
      <SitesFilters q={p.q} setQ={p.setQ} siteType={p.siteType} setSiteType={p.setSiteType} status={p.status} setStatus={p.setStatus} siteTypes={p.types} missing={p.missing} />
      {p.isAdmin && <MissingTypeBanner count={p.missing} active={p.onlyMissing} onShow={() => p.setSiteType(p.onlyMissing ? "" : NO_TYPE)} />}
      <DataState loading={p.loading && !p.data} empty={!p.sites.length} emptyTitle={p.onlyMissing ? "Tutti i cantieri hanno una tipologia" : "Nessun cantiere"}>
        <SitesBody view={view} sites={p.sites} onOpen={p.openSite} quickType={p.quickType} />
      </DataState>
      <SiteForm open={open} onOpenChange={setOpen} onSaved={(s) => { p.reload(); p.openSite(s.id); }} />
    </div>
  );
}
